/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import { getScopeIdentifier } from '@atlassian/jira-common-navigation/src/get-scope-identifier/index.tsx';
import type { ScopeIdentifier } from '@atlassian/jira-common-navigation/src/get-scope-identifier/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	// eslint-disable-next-line jira/restricted/@atlassian/react-resource-router
	createResource,
	type Match,
	type Query,
	type RouteResource,
	useResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
import type { RouteName } from '@jira/platform__routing-route-name-types/src/RouteName.tsx';
import { getBoardId } from '@jira/router-resources__utils/src/getBoardId.tsx';
import { getProjectKeyId } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import { fetchProjectSidebarNavInfo } from '../../../services/sidebar/project-sidebar-navigation/main.tsx';
import type { ProjectSidebarNavInfo } from '../../../services/sidebar/project-sidebar-navigation/types.tsx';

export const getIdentifier = (
	routeName: RouteName,
	match: Match,
	query: Query,
): ScopeIdentifier => {
	/**
	 * `boardId` as an identifier should not be returned for JSM boards route,
	 * skip boards on `SERVICEDESK_BOARDS`, `SERVICEDESK_CALENDAR` or `CSM_QUEUES_BOARDS` etc... route.
	 */
	if (fg('csm_appification_standalone_navigation')) {
		const projectKeyOnlyRoutes: RouteName[] = [
			'servicedesk-boards',
			'servicedesk-calendar',
			'servicedesk-queues-custom-board',
			'servicedesk-queues-custom-calendar',
			'servicedesk-queues-practice-custom-board',
			'servicedesk-queues-practice-custom-calendar',
			'servicedesk-queues-views-config',
			'servicedesk-practice-views-config',
			'servicedesk-list',
			'csm-queues-custom-board',
			'csm-queues-custom-calendar',
			'csm-queues-views-config',
		];

		if (projectKeyOnlyRoutes.includes(routeName) || query['servicedesk.serviceDeskId']) {
			const { projectKey, projectId } = getProjectKeyId(match, query);
			return getScopeIdentifier({ projectKey, projectId });
		}

		const { projectKey, projectId } = getProjectKeyId(match, query);
		const boardId = getBoardId(match, query);
		return getScopeIdentifier({ projectKey, projectId, boardId });
	}

	if (
		routeName === 'servicedesk-boards' ||
		routeName === 'servicedesk-calendar' ||
		query['servicedesk.serviceDeskId'] ||
		routeName === 'servicedesk-queues-custom-board' ||
		routeName === 'servicedesk-queues-custom-calendar' ||
		routeName === 'servicedesk-queues-practice-custom-board' ||
		routeName === 'servicedesk-queues-practice-custom-calendar' ||
		routeName === 'servicedesk-queues-views-config' ||
		routeName === 'servicedesk-practice-views-config' ||
		(routeName === 'servicedesk-list' && fg('jsm_list_view_milestone_1'))
	) {
		const { projectKey, projectId } = getProjectKeyId(match, query);
		return getScopeIdentifier({ projectKey, projectId });
	}

	const { projectKey, projectId } = getProjectKeyId(match, query);
	const boardId = getBoardId(match, query);
	return getScopeIdentifier({ projectKey, projectId, boardId });
};

const getRouterKey = (routeName: RouteName, match: Match, query: Query): string => {
	const { identifierType, identifierValue } = getIdentifier(routeName, match, query);
	// Next-Gen software projects contain only one board. Hence, using the project key itself
	if (match.url.includes('/software/projects/')) {
		return `projectKey-${match.params.projectKey || ''}`;
	}
	return identifierType == null || identifierValue == null
		? 'none'
		: `${identifierType}-${identifierValue}`;
};

export const staticProjectSidebarNavigationResource: RouteResource<ProjectSidebarNavInfo | null> =
	createResource<ProjectSidebarNavInfo | null>({
		type: 'NAVIGATION_SIDEBAR_JIRA_PROJECT',
		// oxlint-disable-next-line typescript/consistent-type-assertions
		getKey: ({ match, query, route: { name } }) => getRouterKey(name as RouteName, match, query),
		/**
		 * is called on resource's init fetch, or .refresh()
		 */
		getData: async ({ match, query, route: { name } }) => {
			// oxlint-disable-next-line typescript/consistent-type-assertions
			const scopeIdentifier = getIdentifier(name as RouteName, match, query);
			if (scopeIdentifier.identifierType == null || scopeIdentifier.identifierValue == null) {
				return null;
			}
			const navInfo = await fetchProjectSidebarNavInfo(scopeIdentifier);
			return navInfo;
		},
		maxAge: 5 * 60 * 1000,
		isCritical: true,
	});

export const useSidebarNavigationResource =
	(): UseResourceHookResponse<ProjectSidebarNavInfo | null> =>
		useResource(staticProjectSidebarNavigationResource);
